import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { FaChevronUp, FaFacebookF, FaInstagram, FaLinkedinIn, FaTiktok, FaTwitter, FaYoutube } from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import routes from '@Constants/Routes'
import { useAppSelector } from '@Hooks/ReduxHooks'
import { defaultAssets } from '@Constants/assets.defaults'
type Props = {}

/* abwaab.com palette (Tailwind tokens the marketing site uses) */
const ABW = {
	blue50: '#EFF6FF',
	blue500: '#3B82F6',
	blue600: '#2563EB',
	slate400: '#94A3B8',
	slate500: '#64748B',
	slate700: '#334155',
	black: '#000000',
}

/* abwaab.com externals — the careers link, socials, and country selector stay
   pointed at the real site (locked decision), NOT settings-driven. SOCIALS is
   the single source for BOTH footers so guest and logged-in always match. */
const CAREERS_URL = 'https://abwaab.freshteam.com/jobs/'
const FLAGS = 'https://st.abwaab.com/public/icons/sequare_flags/'
const FOOTER_ILLUSTRATION = 'https://st.abwaab.com/public/images/eg.webp'
const SOCIALS = [
	{ label: 'Instagram', href: 'https://www.instagram.com/abwaabegy', Icon: FaInstagram },
	{ label: 'Facebook', href: 'https://www.facebook.com/abwaabEGY', Icon: FaFacebookF },
	{ label: 'TikTok', href: 'https://www.tiktok.com/@abwaabegy', Icon: FaTiktok },
	{ label: 'Twitter', href: 'https://twitter.com/AbwaabEGY', Icon: FaTwitter },
	{ label: 'LinkedIn', href: 'https://www.linkedin.com/company/abwaab-technologies/', Icon: FaLinkedinIn },
	{ label: 'YouTube', href: 'https://www.youtube.com/@Abwaab.Egypt.Official', Icon: FaYoutube },
]

/* The live selector's option list, in the site's order. Egypt IS this platform
   (no redirect); every other entry goes to the matching abwaab.com site. */
const COUNTRIES: Array<{ ar: string; en: string; flag: string; href: string | null }> = [
	{ ar: 'الأردن', en: 'Jordan', flag: 'jordan.png', href: 'https://abwaab.com/ar-JO' },
	{ ar: 'فلسطين', en: 'Palestine', flag: 'palestine.png', href: 'https://abwaab.com/ar-PS' },
	{ ar: 'مصر', en: 'Egypt', flag: 'egypt.png', href: null },
	{ ar: 'السعودية', en: 'Saudi Arabia', flag: 'saudi.png', href: 'https://abwaab.com/ar-SA' },
	{ ar: 'العراق', en: 'Iraq', flag: 'iraq.png', href: 'https://abwaab.com/ar-IQ' },
	{ ar: 'لغات', en: 'Languages', flag: 'languages.webp', href: 'https://abwaab.com/ar-lang' },
	{ ar: 'قطر', en: 'Qatar', flag: 'Qatar.png', href: 'https://abwaab.com/ar-QA' },
	{ ar: 'عُمان', en: 'Oman', flag: 'Oman.png', href: 'https://abwaab.com/ar-OM' },
	{ ar: 'مناهج دولية', en: 'International', flag: 'int.webp', href: 'https://abwaab.com/ar-int' },
	{ ar: 'الكويت', en: 'Kuwait', flag: 'kuwait.svg', href: 'https://abwaab.com/ar-KW' },
	{ ar: 'المغرب', en: 'Morocco', flag: 'morocco.svg', href: 'https://abwaab.com/ar-MA' },
]

/** Replica of the site's country dropdown. Egypt (current) → our home page. */
const CountrySelect: React.FC = () => {
	const [open, setOpen] = useState(false)
	const wrapRef = useRef<HTMLDivElement>(null)
	const history = useNavigate()
	const { i18n } = useTranslation()
	const isAr = (i18n.language || '').toLowerCase().startsWith('ar')
	const egypt = COUNTRIES.find(c => c.href === null)!
	useEffect(() => {
		if (!open) return
		const onDocClick = (e: MouseEvent) => {
			if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
		}
		document.addEventListener('mousedown', onDocClick)
		return () => document.removeEventListener('mousedown', onDocClick)
	}, [open])
	return (
		<SelectWrap id="footer-country-select" ref={wrapRef}>
			<SelectTrigger type="button" aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen(!open)}>
				<span className="current">
					<img alt="" src={FLAGS + egypt.flag} />
					<span>{isAr ? egypt.ar : egypt.en}</span>
				</span>
				<span className={`chevron ${open ? 'open' : ''}`}>
					<FaChevronUp />
				</span>
			</SelectTrigger>
			{open && (
				<SelectList role="listbox">
					{COUNTRIES.map(c =>
						c.href ? (
							<a key={c.en} role="option" aria-selected={false} href={c.href}>
								<img alt="" src={FLAGS + c.flag} />
								<span>{isAr ? c.ar : c.en}</span>
							</a>
						) : (
							<button
								key={c.en}
								role="option"
								aria-selected={true}
								type="button"
								onClick={() => {
									setOpen(false)
									history(routes.home)
								}}
							>
								<img alt="" src={FLAGS + c.flag} />
								<span>{isAr ? c.ar : c.en}</span>
							</button>
						)
					)}
				</SelectList>
			)}
		</SelectWrap>
	)
}

/**
 * Guest footer — abwaab.com replica: light-blue rounded-top band, logo+socials
 * row, then المزيد / الاحكام / البلد columns with the country dropdown and the
 * site's illustration. No copyright line (replica).
 */
const GuestFooter: React.FC = () => {
	const [t] = useTranslation('layouts', { keyPrefix: 'footer' })
	const history = useNavigate()
	const { assets, assetsLoading } = useAppSelector(s => s.settings)
	return (
		<ReplicaFooter>
			<ReplicaInner>
				<BrandRow>
					{!assetsLoading && <img alt="abwaab" src={assets.logo ?? defaultAssets.logo} />}
					<ul>
						{SOCIALS.map(({ label, href, Icon }) => (
							<li key={label}>
								<a aria-label={label} href={href} target="_blank" rel="noopener noreferrer">
									<Icon />
								</a>
							</li>
						))}
					</ul>
				</BrandRow>
				<ColumnsRow>
					<Illustration alt="" src={FOOTER_ILLUSTRATION} />
					<ReplicaColumn>
						<h3>{t('more', { defaultValue: 'المزيد' })}</h3>
						<ReplicaLinkButton type="button" onClick={() => history(routes.aboutUs)}>
							{t('about_abwaab', { defaultValue: 'عن أبواب' })}
						</ReplicaLinkButton>
						<ReplicaLinkButton type="button" onClick={() => history(routes.ourTeam)}>
							{t('our_team', { defaultValue: 'فريقنا' })}
						</ReplicaLinkButton>
						<a href={CAREERS_URL} target="_blank" rel="noopener noreferrer">
							{t('careers', { defaultValue: 'وظائف' })}
						</a>
						<ReplicaLinkButton type="button" onClick={() => history(routes.contactUs)}>
							{t('contact', { defaultValue: 'تواصل معنا' })}
						</ReplicaLinkButton>
					</ReplicaColumn>
					<ReplicaColumn>
						<h3>{t('policies', { defaultValue: 'الاحكام' })}</h3>
						<ReplicaLinkButton type="button" onClick={() => history(routes.termsOfUse)}>
							{t('terms_of_use', { defaultValue: 'شروط الاستخدام' })}
						</ReplicaLinkButton>
						<ReplicaLinkButton type="button" onClick={() => history(routes.privacyPolicy)}>
							{t('privacy_policy', { defaultValue: 'اتفاقية الخصوصية' })}
						</ReplicaLinkButton>
					</ReplicaColumn>
					<ReplicaColumn>
						<h3>{t('country', { defaultValue: 'البلد' })}</h3>
						<CountrySelect />
					</ReplicaColumn>
				</ColumnsRow>
			</ReplicaInner>
		</ReplicaFooter>
	)
}

/**
 * Logged-in footer — the social row renders the same SOCIALS as the guest
 * footer (the settings-driven links fell back to stale Eduact accounts and
 * let the two surfaces disagree).
 */
const AuthedFooter: React.FC = () => {
	const [t] = useTranslation('layouts', { keyPrefix: 'footer' })
	const history = useNavigate()
	const { about_us_page } = useAppSelector(s => s.settings)
	return (
		<FooterStyled>
			<FooterLinksRow>
				{about_us_page && (
					<FooterLinkButton type="button" onClick={() => history('/about')}>
						{t('aboutUs')}
					</FooterLinkButton>
				)}
				<FooterLinkButton type="button" onClick={() => history(routes.privacyPolicy)}>
					{t('privacy')}
				</FooterLinkButton>
				<FooterLinkButton type="button" onClick={() => history(routes.termsOfUse)}>
					{t('terms')}
				</FooterLinkButton>
				<FooterLinkButton type="button" onClick={() => history(routes.faqs)}>
					{t('FAQs')}
				</FooterLinkButton>
			</FooterLinksRow>
			<FooterSocialRow>
				{SOCIALS.map(({ label, href, Icon }) => (
					<SocialMediaIcon key={label} aria-label={label} href={href} target="_blank" rel="noopener noreferrer">
						<Icon />
					</SocialMediaIcon>
				))}
			</FooterSocialRow>
			<FooterCopyRights>{t('copy_rights')}</FooterCopyRights>
		</FooterStyled>
	)
}

const Footer: React.FC<Props> = () => {
	const { isAuth } = useAppSelector(s => s.auth)
	return isAuth ? <AuthedFooter /> : <GuestFooter />
}

export default Footer

/* ── guest replica (bg-blue-50 rounded-t-3xl) ──────────────────── */

/* no overflow:hidden — it clipped the open country dropdown */
const ReplicaFooter = styled.footer`
	margin-top: 4rem;
	width: 100%;
	background: ${ABW.blue50};
	border-radius: 1.5rem 1.5rem 0 0;
	z-index: 2;
`

const ReplicaInner = styled.div`
	position: relative;
	max-width: 1500px;
	margin: 0 auto;
	padding: 1rem 1rem 4rem;
	@media (min-width: 768px) {
		padding: 2rem 1.5rem 4rem;
	}
	@media (min-width: 1024px) {
		padding-inline: 3.5rem;
	}
`

/* logo + white-circle social icons (blue glyphs), like the live band */
const BrandRow = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: space-between;
	gap: 1rem;
	@media (min-width: 768px) {
		flex-direction: row;
	}
	img {
		width: 134px;
		object-fit: contain;
		@media (min-width: 768px) {
			width: 188px;
		}
	}
	ul {
		display: flex;
		list-style: none;
		margin: 0;
		padding: 0;
	}
	li {
		margin: 0 0.25rem;
	}
	li a {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 38px;
		height: 38px;
		border-radius: 9999px;
		background: #fff;
		color: ${ABW.blue500};
		@media (min-width: 768px) {
			width: 44px;
			height: 44px;
		}
		svg {
			width: 20px;
			height: 20px;
			@media (min-width: 768px) {
				width: 24px;
				height: 24px;
			}
		}
	}
`

const ColumnsRow = styled.div`
	display: flex;
	flex-direction: column;
	margin-top: 1.5rem;
	@media (min-width: 768px) {
		flex-direction: row;
		justify-content: flex-start;
		gap: 5rem;
		margin-top: 2.5rem;
	}
`

/* the site's Egypt illustration: full-width first on mobile, pinned to the
   inline-end bottom corner on desktop */
const Illustration = styled.img`
	order: -1;
	width: 100%;
	@media (min-width: 768px) {
		order: 10;
		position: absolute;
		bottom: 2.5rem;
		inset-inline-end: 2.25rem;
		width: 450px;
	}
`

const ReplicaColumn = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	margin-top: 1.75rem;
	@media (min-width: 768px) {
		margin-top: 0;
	}
	h3 {
		margin: 0 0 1rem;
		font: var(--fw-bold) 1.125rem var(--font-sans);
		color: ${ABW.blue600};
		@media (min-width: 768px) {
			font-size: 22px;
		}
	}
	a {
		white-space: nowrap;
		margin-bottom: 1rem;
		cursor: pointer;
		font: var(--fw-regular) 1rem var(--font-sans);
		color: ${ABW.black};
		text-decoration: none;
		@media (min-width: 768px) {
			font-size: 1.125rem;
		}
		&:hover {
			color: ${ABW.blue500};
		}
	}
`

const ReplicaLinkButton = styled.button`
	border: none;
	background: none;
	padding: 0;
	margin-bottom: 1rem;
	cursor: pointer;
	white-space: nowrap;
	text-align: start;
	font: var(--fw-regular) 1rem var(--font-sans);
	color: ${ABW.black};
	@media (min-width: 768px) {
		font-size: 1.125rem;
	}
	&:hover {
		color: ${ABW.blue500};
	}
`

/* ── country dropdown replica ──────────────────────────────────── */

const SelectWrap = styled.div`
	position: relative;
	width: 100%;
	min-width: 250px;
	max-width: 330px;
	user-select: none;
`

const SelectTrigger = styled.button`
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	min-height: 48px;
	padding: 0.25rem 1rem;
	border: 1px solid ${ABW.slate400}33;
	border-radius: 0.5rem;
	background: #fff;
	cursor: pointer;
	font-family: var(--font-sans);
	.current {
		display: flex;
		align-items: center;
		gap: 0.25rem;
		color: ${ABW.slate700};
		font-size: 0.875rem;
		@media (min-width: 768px) {
			font-size: 1rem;
		}
		img {
			width: 28px;
			height: 28px;
			object-fit: contain;
		}
		span {
			margin: 0 0.5rem;
		}
	}
	.chevron {
		display: flex;
		align-items: center;
		color: ${ABW.slate500};
		transition: transform 300ms;
		transform: rotate(180deg);
		svg {
			width: 12px;
			height: 12px;
		}
		&.open {
			transform: rotate(0deg);
		}
	}
`

/* opens downward below the trigger, like the live site */
const SelectList = styled.div`
	position: absolute;
	top: calc(100% + 4px);
	inset-inline: 0;
	max-height: 320px;
	overflow-y: auto;
	border: 1px solid ${ABW.slate400}33;
	border-radius: 0.5rem;
	background: #fff;
	box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
	z-index: 5;
	a,
	button {
		display: flex;
		align-items: center;
		gap: 0.75rem;
		width: 100%;
		padding: 0.5rem;
		border: none;
		border-bottom: 1px solid ${ABW.slate400}55;
		background: #fff;
		cursor: pointer;
		text-decoration: none;
		font: var(--fw-regular) 0.875rem var(--font-sans);
		color: ${ABW.slate700};
		text-align: start;
		@media (min-width: 640px) {
			padding: 0.75rem 0.5rem;
			font-size: 1rem;
		}
		&:hover {
			background: ${ABW.blue50};
		}
		img {
			width: 28px;
			height: 28px;
			object-fit: contain;
		}
	}
`

/* ── logged-in footer (unchanged design) ───────────────────────── */

const FooterStyled = styled.footer`
	margin-top: auto;
	border-top: 1px solid var(--border);
	background: var(--surface-card);
	padding: var(--space-4);
	display: flex;
	flex-direction: column;
	gap: 10px;
	align-items: center;
	z-index: 2;
`

const FooterLinksRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	justify-content: center;
	gap: var(--space-6);
	a {
		font: var(--fw-bold) var(--fs-label) var(--font-sans);
		color: var(--blue);
		text-decoration: none;
		&:hover {
			color: var(--blue-hover);
		}
	}
`

const FooterLinkButton = styled.button`
	border: none;
	background: none;
	padding: 0;
	cursor: pointer;
	font: var(--fw-bold) var(--fs-label) var(--font-sans);
	color: var(--blue);
	&:hover {
		color: var(--blue-hover);
	}
`

const FooterSocialRow = styled.div`
	display: flex;
	align-items: center;
	gap: var(--space-4);
`

const SocialMediaIcon = styled.a`
	cursor: pointer;
	color: var(--text-secondary);
	display: flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	border-radius: var(--r-full);
	transition: color var(--dur-fast) var(--ease), background var(--dur-fast) var(--ease);
	svg {
		width: 14px;
		height: 14px;
	}
	&:hover {
		color: var(--blue);
		background: var(--blue-08);
	}
`

const FooterCopyRights = styled.section`
	font: var(--fw-regular) var(--fs-micro-plus) var(--font-sans);
	color: var(--text-secondary);
	text-align: center;
`
